import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@atgis/ui";
import { Cloud, Server } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";

const ROWS: Array<{ label: string; cloud: string; onprem: string }> = [
  {
    label: "Kurulum",
    cloud: "Saniyeler içinde, kayıtla başlar",
    onprem: "RADGIS mühendislik ekibi tarafından",
  },
  {
    label: "Veri yeri",
    cloud: "RADGIS yönetimli bulut altyapısı",
    onprem: "Müşterinin kendi veri merkezi / iç ağı",
  },
  {
    label: "Tier yükseltme",
    cloud: "Self-service (Free → Basic → Pro → Enterprise)",
    onprem: "Tümü Free başlar; sistem yöneticisi elle yükseltir",
  },
  {
    label: "Dosya depolama",
    cloud: "Yönetilen depolama; kotalar plan bazlı (Pro 10 GB, Enterprise 500 GB)",
    onprem:
      "Kurumun kendi S3 uyumlu nesne depolama sunucusuna bağlanır; kotalar sistem yöneticisi tarafından serbestçe belirlenir",
  },
  {
    label: "Güncellemeler",
    cloud: "Otomatik, sıfır kesinti",
    onprem: "Sürüm planına göre kontrollü güncelleme",
  },
  { label: "Yedekleme", cloud: "Yönetilen, çoklu bölge", onprem: "Müşteri politikasına göre" },
  {
    label: "Erişim",
    cloud: "İnternet üzerinden, TLS",
    onprem: "Kurum iç ağı, VPN veya tamamen izole (air-gapped) ortam",
  },
];

export function Deployment() {
  return (
    <section id="kurulum" className="border-b border-border/60 bg-card/30">
      <div className="container py-20 md:py-28">
        <SectionHeading
          eyebrow="Kurulum Modelleri"
          title={
            <>
              Cloud veya <span className="gradient-text">on-premise</span>, aynı platform.
            </>
          }
          description="Veri egemenliği gerektiren kurumlar için on-premise dağıtım, hızla başlamak isteyenler için yönetilen cloud. İki modelde de aynı kod, aynı yetenekler."
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <Card className="relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                  <Cloud className="h-5 w-5" />
                </div>
                <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary">
                  Yönetilen
                </span>
              </div>
              <CardTitle className="mt-4 text-xl">RADGIS Cloud</CardTitle>
              <CardDescription>
                Kayıt ol, e-postanı doğrula, dakikalar içinde projelerini açmaya başla. Self-service
                plan yönetimi ve otomatik güncellemeler.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Free → Basic → Pro → Enterprise self-service yükseltme</li>
                <li>• %99.95 SLA hedefi, çoklu bölge yedekleme</li>
                <li>• Sıfır altyapı bakımı</li>
                <li>• Avrupa/Türkiye veri merkezi seçeneği</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--project-master))] to-transparent" />
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-[hsl(var(--project-master))]/10 text-[hsl(var(--project-master))]">
                  <Server className="h-5 w-5" />
                </div>
                <span className="rounded-full bg-[hsl(var(--project-master))]/10 px-2.5 py-0.5 text-[11px] font-medium text-[hsl(var(--project-master))]">
                  Veri sizde
                </span>
              </div>
              <CardTitle className="mt-4 text-xl">RADGIS On-Premise</CardTitle>
              <CardDescription>
                Veri egemenliği, ağ izolasyonu veya regülasyon gereksinimleri için kurumun kendi
                altyapısında çalışan tam kurulum. Cloud sürümüyle farkı: self-service plan yönetimi
                yok, depolama ve kotalar tamamen kurumun elinde.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Tüm hesaplar Free başlar; sistem yöneticisi planları elle yükseltir</li>
                <li>• Kurumun kendi S3 uyumlu nesne depolama sunucusuna bağlanır</li>
                <li>• Depolama kotaları sistem yöneticisi tarafından serbestçe belirlenir</li>
                <li>
                  • Kurum iç ağında, VPN arkasında veya tamamen izole (air-gapped) ortamda çalışır
                </li>
                <li>• Müşteri yedekleme ve disaster-recovery politikası</li>
                <li>• RADGIS destekli kurulum ve eğitim paketi</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-border/60 bg-background">
          <div className="grid grid-cols-3 border-b border-border/60 bg-muted/40 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <div>Karşılaştırma</div>
            <div>Cloud</div>
            <div>On-Premise</div>
          </div>
          {ROWS.map((row, idx) => (
            <div
              key={row.label}
              className={
                "grid grid-cols-3 px-6 py-4 text-sm " + (idx % 2 === 1 ? "bg-card/30" : "")
              }
            >
              <div className="font-medium text-foreground/80">{row.label}</div>
              <div className="text-muted-foreground">{row.cloud}</div>
              <div className="text-muted-foreground">{row.onprem}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
